import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDate, getDatePlusMinus } from '@einhaus/meteoric-helpers';
import type { BarDuration, EntityTypeUnion } from '@einhaus/project314-types';
import ChartContainer from '@/components/Chart/ChartContainer';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { getEntityLink } from '@/utils/entityHelpers';
import { hideInitialSpinner } from '@/utils/spinnerControl';

type OracleRenderVariant = 'rawFocused' | 'rawWide';

const ORACLE_RENDER_VARIANTS: OracleRenderVariant[] = ['rawWide', 'rawFocused'];
const CHART_LOADING_READY_SELECTOR = '.chart-container[data-chart-loading-state="ready"]';
const CHART_LOADING_EMPTY_SELECTOR = '.chart-container[data-chart-loading-state="empty"]';
const CHART_LOADING_ERROR_SELECTOR = '.chart-container[data-chart-loading-state="error"]';
const CHART_LOADING_OVERLAY_SELECTOR = '[data-chart-loading-overlay="true"]';
const MIN_CAPTURE_CANVAS_WIDTH = 500;
const MIN_CAPTURE_CANVAS_HEIGHT = 200;
const MIN_CAPTURE_READY_CANVAS_COUNT = 2;

const isOracleRenderVariant = (value: string | null): value is OracleRenderVariant =>
    value !== null && ORACLE_RENDER_VARIANTS.includes(value as OracleRenderVariant);

const isBarDuration = (value: string | null): value is BarDuration => {
    if (!value) return false;
    return ['1m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d', '1w', '1mo'].includes(value);
};

const isEntityType = (value: string | null): value is EntityTypeUnion['entityType'] => {
    if (!value) return false;
    return ['Asset', 'Cryptocurrency', 'AssetIndex'].includes(value);
};

const getOracleWindow = (params: { duration: BarDuration; snapshotDateTime: string; variant: OracleRenderVariant }) => {
    const { duration, snapshotDateTime, variant } = params;

    if (duration === '1w') {
        const lookbackDays = variant === 'rawWide' ? 1400 : 700;
        return {
            endDateTime: snapshotDateTime,
            startDateTime: getDatePlusMinus('-', lookbackDays, 'day', 'ymdhms', 'start', snapshotDateTime)
        };
    }

    const lookbackDays = variant === 'rawWide' ? 540 : 270;
    return {
        endDateTime: snapshotDateTime,
        startDateTime: getDatePlusMinus('-', lookbackDays, 'day', 'ymdhms', 'start', snapshotDateTime)
    };
};

const ChartPatternAssetOracleRender = () => {
    const { assetId = '' } = useParams<{ assetId: string }>();
    const [searchParams] = useSearchParams();
    const parsedAssetId = Number(assetId);
    const requestedVariant = searchParams.get('variant');
    const requestedDuration = searchParams.get('duration');
    const requestedEntityType = searchParams.get('entityType');
    const snapshotDateTime = searchParams.get('snapshotDateTime') ?? getDate({ format: 'ymdhms' });
    const requestedStartDateTime = searchParams.get('startDateTime');
    const requestedEndDateTime = searchParams.get('endDateTime');
    const captureMode = searchParams.get('capture') === '1' || searchParams.get('capture')?.toLowerCase() === 'true';
    const variant: OracleRenderVariant = requestedVariant && isOracleRenderVariant(requestedVariant) ? requestedVariant : 'rawFocused';
    const duration: BarDuration = requestedDuration && isBarDuration(requestedDuration) ? requestedDuration : '1d';
    const entityType: EntityTypeUnion['entityType'] =
        requestedEntityType && isEntityType(requestedEntityType) ? requestedEntityType : 'Asset';
    const captureFrameRef = useRef<HTMLDivElement | null>(null);
    const [hasChartRenderSettled, setHasChartRenderSettled] = useState(false);
    const [isCaptureReady, setIsCaptureReady] = useState(false);
    const [renderFailureReason, setRenderFailureReason] = useState<string | null>(null);

    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityId: Number.isInteger(parsedAssetId) && parsedAssetId > 0 ? parsedAssetId : 0,
            entityType
        }),
        enabled: Number.isInteger(parsedAssetId) && parsedAssetId > 0
    });

    const entity = entityQuery.data?.entity ?? null;

    const chartWindow = useMemo(() => {
        if (requestedStartDateTime && requestedEndDateTime) {
            return {
                endDateTime: requestedEndDateTime,
                startDateTime: requestedStartDateTime
            };
        }

        return getOracleWindow({
            duration,
            snapshotDateTime,
            variant
        });
    }, [duration, requestedEndDateTime, requestedStartDateTime, snapshotDateTime, variant]);

    useEffect(() => {
        hideInitialSpinner();
    }, []);

    useEffect(() => {
        setHasChartRenderSettled(false);
        setIsCaptureReady(false);
        setRenderFailureReason(null);
    }, [parsedAssetId, duration, entityType, snapshotDateTime, variant]);

    useEffect(() => {
        if (!hasChartRenderSettled) return;

        let animationFrameId: number | null = null;

        const evaluateCaptureReadiness = () => {
            const captureFrame = captureFrameRef.current;

            if (!captureFrame) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            const chartContainer = captureFrame.querySelector(CHART_LOADING_READY_SELECTOR);
            const emptyChartContainer = captureFrame.querySelector(CHART_LOADING_EMPTY_SELECTOR);
            const errorChartContainer = captureFrame.querySelector(CHART_LOADING_ERROR_SELECTOR);
            const loadingOverlay = captureFrame.querySelector(CHART_LOADING_OVERLAY_SELECTOR);

            if (loadingOverlay) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            if (errorChartContainer) {
                setRenderFailureReason('chart-error');
                return;
            }

            if (emptyChartContainer) {
                setRenderFailureReason('no-bars');
                return;
            }

            if (!chartContainer) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            const readyCanvases = Array.from(chartContainer.querySelectorAll('canvas')).filter((canvas) => {
                return canvas.width >= MIN_CAPTURE_CANVAS_WIDTH && canvas.height >= MIN_CAPTURE_CANVAS_HEIGHT;
            });

            if (readyCanvases.length < MIN_CAPTURE_READY_CANVAS_COUNT) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    setIsCaptureReady(true);
                });
            });
        };

        animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [hasChartRenderSettled]);

    if (!Number.isInteger(parsedAssetId) || parsedAssetId <= 0) {
        return <div className="p-6 text-red-300">Invalid asset id.</div>;
    }

    if (entityQuery.isLoading) {
        return (
            <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
                <DataLoadingSpinner text="Loading chart-pattern oracle render..." />
            </div>
        );
    }

    if (entityQuery.error) {
        return <div className="p-6 text-red-300">{entityQuery.error.message}</div>;
    }

    if (!entity) {
        return <div className="p-6 text-zinc-300">Asset not found.</div>;
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white px-6 py-5">
            <div className="max-w-[1800px] mx-auto space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Chart Pattern Oracle Render</div>
                        <h1 className="text-3xl font-semibold">
                            {entity.symbol} <span className="text-zinc-500 text-xl">{entity.name ?? ''}</span>
                        </h1>
                        <div className="text-sm text-zinc-400">
                            entityId={entity.id} · entityType={entityType} · duration={duration} · snapshot={snapshotDateTime} · variant=
                            {variant}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <Link className="text-blue-400 hover:text-blue-300 underline" to={`${getEntityLink(entity)}?duration=${duration}`}>
                            Open Full Entity Page
                        </Link>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {ORACLE_RENDER_VARIANTS.map((candidateVariant) => (
                        <Link
                            className={`rounded border px-3 py-1 ${
                                candidateVariant === variant
                                    ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                    : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                            }`}
                            key={candidateVariant}
                            to={`/tools/chartPatternAudit/rawAssetRender/${entity.id}?entityType=${encodeURIComponent(entityType)}&variant=${candidateVariant}&duration=${duration}&snapshotDateTime=${encodeURIComponent(
                                snapshotDateTime
                            )}${captureMode ? '&capture=1' : ''}`}
                        >
                            {candidateVariant}
                        </Link>
                    ))}
                </div>

                <div
                    className="mx-auto min-h-[980px] w-[1728px] max-w-[1728px]"
                    data-render-failure-reason={renderFailureReason ?? undefined}
                    data-render-ready={captureMode ? (isCaptureReady ? 'true' : 'false') : undefined}
                    id="chartPatternAuditCaptureFrame"
                    ref={captureFrameRef}
                >
                    <ChartContainer
                        hideControls
                        duration={duration}
                        endDateTime={chartWindow.endDateTime}
                        entity={entity}
                        shouldIncludeChartPatterns={false}
                        shouldRenderChartPatternEventMarkers={false}
                        shouldShowChartPatternPanel={false}
                        startDateTime={chartWindow.startDateTime}
                        onInitialRenderSettled={() => setHasChartRenderSettled(true)}
                    />
                </div>
            </div>
        </div>
    );
};

export default ChartPatternAssetOracleRender;
